import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * useUserPrefs — Firestore-backed user preferences.
 *
 * Stored at users/{uid}/preferences/main as a single doc — preferences are
 * small and read together, so a doc per key would just multiply round
 * trips for no benefit.
 *
 * What lives here vs in useSettings:
 *   - useSettings   = device-local (theme, palette, font size, line height).
 *                     Stored in localStorage, doesn't sync across devices.
 *   - useUserPrefs  = user-wide (designated reminder list, notification
 *                     preferences, quiet hours). Synced via Firestore.
 *
 * Defaults are merged in at read time so callers can safely access any
 * field — they don't have to handle "doc doesn't exist yet" themselves.
 *
 * The returned `prefs` is `undefined` while the initial snapshot is in
 * flight (matches the convention in useAuth). Once loaded it's always an
 * object with all default keys present.
 */

// Default values for every pref key. Spread into the loaded data so
// missing fields (older user accounts, fresh signups, etc.) don't trip
// callers up.
const DEFAULTS = Object.freeze({
  // Master switch for the reminders feature. Default OFF so the
  // in-progress UI doesn't appear for production users while chunks
  // 4-8 are being built. Walter (or any tester) can flip this true
  // via either:
  //   1. Editing users/{uid}/preferences/main in Firestore Console,
  //   2. Or via localStorage: localStorage.setItem('lists_reminders_dev', '1')
  //      then refresh — the localStorage path is faster for dev iteration.
  reminderFeatureEnabled: false,
  // — Reminder feature —
  // List IDs that are designated as "everything in here is a reminder"
  // surfaces. Plural so users can have multiple (work + personal,
  // etc.) without juggling settings. The legacy singular field
  // `designatedReminderListId` is still read for backward compatibility
  // (see snapshot normalizer below) but new code should use the plural.
  designatedReminderListIds: [],
  // Legacy: singular designated list. Kept readable so we can migrate
  // older docs into the plural field at snapshot time. New writes
  // should set `designatedReminderListIds` instead.
  designatedReminderListId: null,
  // We also store the boardId so we can navigate to the list without
  // having to scan all boards looking for it.
  designatedReminderBoardId: null,
  // List IDs that auto-stamp `@today` on every new line — the "log
  // list" designation. Independent of the reminder-list designation;
  // a list can be both, neither, or either. Lines with `@<date>`
  // surface in the LogPanel regardless of whether their list is
  // designated, so this prefs only controls the auto-stamp behaviour.
  designatedLogListIds: [],
  // — Notification / delivery —
  // Default time to use when a reminder has a date but no explicit
  // hour (e.g. "tomorrow call mom" → tomorrow at this time). 7am is
  // a sensible default; users can change in Settings. Stored as
  // "HH:MM" 24-hour to match HTML5 time-input format.
  defaultReminderTime: '07:00',
  // Push: default ON. The user still has to grant browser permission
  // before this actually does anything.
  deliverPush: true,
  // Email: default ON. Walter 2026-05-06 round 8: most users skipped
  // turning this on and then assumed reminders were broken because
  // push permission alone didn't cover them across devices. Defaulting
  // to on means a reminder always reaches the user; the toggle in
  // Settings is the opt-out for anyone who specifically doesn't
  // want email.
  deliverEmail: true,
  // Quiet hours OFF by default — we trust the user's reminder times.
  // When user enables, they pick a window. No push during it.
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '07:00',
  // — Reminder digests — Walter 2026-05-14
  // Email summaries (additive — does NOT suppress per-reminder
  // emails when on). User opts in via Settings → Reminders.
  // Daily: fires once per day at the configured hour in user's
  // local timezone. Includes today's reminders + overdue +
  // undated. Weekly: fires on configured day-of-week at the
  // configured hour, includes the upcoming week.
  digestDailyEnabled: false,
  digestDailyHour: 7,           // 0–23, local TZ
  digestWeeklyEnabled: false,
  digestWeeklyDayOfWeek: 1,     // 0=Sun..6=Sat — default Monday
  digestWeeklyHour: 8,          // 0–23, local TZ — default 8 AM (Walter 2026-05-16: was 6 PM, but Monday morning lands at the right time for "here's your week" framing)
  // IANA timezone string (e.g. "America/New_York"). Used by the
  // reminder-fire Cloud Function so recurring reminders keep their
  // local time-of-day across DST transitions. Auto-detected from
  // Intl.DateTimeFormat().resolvedOptions().timeZone on first load
  // (see App.js). Null means "fall back to UTC math" — same
  // behaviour as before the field was introduced.
  timezone: null,
  // — Sharing & collaboration (alpha) —
  // Master switch for the in-progress sharing UI. Default OFF for
  // production users; opt-in via Settings → Sharing → on. When OFF,
  // share buttons / "shared with me" section / lock indicators are
  // all hidden, and the data-model fields (editors/viewers/editLock)
  // are still written/read but never surfaced. This lets us ship the
  // foundation without exposing the half-built UX.
  //
  // Flag lives in userPrefs (Firestore-synced) rather than
  // localStorage so once a user opts in it carries across devices.
  sharingAlphaEnabled: false,
  // — Terms-of-Service click-accept (Walter 2026-05-15) —
  // Version string + epoch-ms timestamp captured when the user ticked
  // the agreement checkbox at sign-in. Written by App.js's post-auth
  // flusher (consumePendingConsent → updatePrefs). Null means
  // "never click-accepted" — for pre-2026-05-15 users, consent is
  // implied by continued use under prior Terms. Source of truth for
  // the version string lives at src/lib/termsVersion.js; bump it
  // there when Terms or Privacy is materially changed.
  termsAcceptedVersion: null,
  termsAcceptedAt: null,
});

export function useUserPrefs(userId) {
  // undefined = loading; once loaded, always an object with defaults
  // applied.
  const [prefs, setPrefs] = useState(undefined);

  useEffect(() => {
    if (!userId) {
      setPrefs(undefined);
      return undefined;
    }
    const ref = doc(db, 'users', userId, 'preferences', 'main');
    const unsub = onSnapshot(ref, (snap) => {
      const raw = snap.exists() ? snap.data() : {};
      const merged = { ...DEFAULTS, ...raw };
      // Normalize legacy singular → plural. We don't write the migration
      // back automatically; if the user touches Settings or creates a
      // new designated list, the plural field gets persisted. Until then
      // we just present the array as derived state.
      if ((!Array.isArray(merged.designatedReminderListIds)
           || merged.designatedReminderListIds.length === 0)
          && merged.designatedReminderListId) {
        merged.designatedReminderListIds = [merged.designatedReminderListId];
      }
      if (!Array.isArray(merged.designatedReminderListIds)) {
        merged.designatedReminderListIds = [];
      }
      if (!Array.isArray(merged.designatedLogListIds)) {
        merged.designatedLogListIds = [];
      }
      setPrefs(merged);
    }, (err) => {
      console.error('useUserPrefs snapshot error:', err);
      // Fall back to defaults so the app stays usable even if the
      // preferences read fails.
      setPrefs({ ...DEFAULTS });
    });
    return unsub;
  }, [userId]);

  /**
   * updatePrefs(patch) — partial update with merge. Pass any subset of
   * keys; missing keys remain untouched.
   */
  const updatePrefs = useCallback(async (patch) => {
    if (!userId) return;
    const ref = doc(db, 'users', userId, 'preferences', 'main');
    await setDoc(ref, patch, { merge: true });
  }, [userId]);

  return { prefs, updatePrefs };
}

/**
 * isDesignatedReminderList — single read-anywhere check for "is this
 * list one of the user's designated reminder lists?" Reads the plural
 * `designatedReminderListIds` array, falling back to the legacy
 * singular field for older accounts the snapshot normalizer hasn't
 * touched yet (defensive — useUserPrefs DOES normalize, but components
 * may receive prefs from other paths).
 */
export function isDesignatedReminderList(userPrefs, listId) {
  if (!userPrefs || !listId) return false;
  if (Array.isArray(userPrefs.designatedReminderListIds)
      && userPrefs.designatedReminderListIds.includes(listId)) {
    return true;
  }
  if (userPrefs.designatedReminderListId === listId) return true;
  return false;
}

/**
 * isDesignatedLogList — parallel to isDesignatedReminderList. A list
 * designated as a "log list" auto-prepends `@today` to new lines.
 * Lines with `@<date>` surface in the LogPanel regardless of this
 * flag — designation is purely about the auto-stamp ergonomic.
 */
export function isDesignatedLogList(userPrefs, listId) {
  if (!userPrefs || !listId) return false;
  return Array.isArray(userPrefs.designatedLogListIds)
    && userPrefs.designatedLogListIds.includes(listId);
}

/**
 * getListEmptyHint — Walter 2026-06-16. The example/instruction text for
 * reminder + log lists used to be SEEDED AS CONTENT, which meant those
 * lines got auto-captured as reminders / @date-stamped once the user made
 * their first edit (the seededFromTemplate flag only suppressed capture
 * until then). Walter's fix: don't seed instructions as content at all —
 * the list starts empty and shows these as a textarea PLACEHOLDER, which
 * can never be captured, sorted, or persisted. Returns the multi-line
 * placeholder string for a designated reminder/log list, else '' (plain
 * lists keep the browser's no-placeholder default).
 *
 * Newlines render in a <textarea> placeholder across modern browsers.
 */
export function getListEmptyHint(userPrefs, listId) {
  if (isDesignatedReminderList(userPrefs, listId)) {
    // Walter 2026-08-25: teach the PARAGRAPH rule (#204) — "every line becomes
    // a reminder" is now only half of it. The example SHOWS the rule rather
    // than only stating it: "bring the forms" sits under the reminder as its
    // note, then a blank line starts the next one.
    return 'Every line here becomes a reminder.\n'
      + 'Lines right under one are its notes.\n'
      + 'Leave a blank line to start the next reminder.\n'
      + '\n'
      + 'Try:  call mom tomorrow 3pm\n'
      + '      bring the forms\n'
      + '\n'
      + '      every weekday 7am — review priorities\n'
      + '\n'
      + 'Tap the 🔔 up top to see them all.';
  }
  if (isDesignatedLogList(userPrefs, listId)) {
    return 'Every line here gets stamped with today’s date.\n'
      + 'Try:  finished design draft\n'
      + '      shipped the auth fix\n'
      + '\n'
      + '@yesterday or @Sun work too.';
  }
  return '';
}

/**
 * isRemindersEnabled — single read-anywhere check for the reminders
 * feature flag. Walter 2026-05-06 round 8: always returns true. The
 * old per-user toggle ("reminders on/off" in Settings) was removed —
 * if you don't want reminders, don't add a 🔔 line. Kept as a
 * function so callers don't all need to be touched; the
 * `userPrefs` parameter is now ignored.
 */
// eslint-disable-next-line no-unused-vars
export function isRemindersEnabled(userPrefs) {
  return true;
}
